import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  isIosMobile,
  isMobileWeb,
  isAndroidMobile,
  isPifAppInstalled,
  isPifAppInstalledSync,
  openContinueApp,
  watchForPifAppInstall,
  subscribePifInstall,
} from '../utils/seo';
import appIcon from '../Assets/logo.png';
import './PropertyAppPrompt.css';

const MODAL_DISMISS_PREFIX = 'pif_prop_app_modal_dismissed_';
const BAR_DISMISS_PREFIX = 'pif_prop_app_bar_dismissed_';

/**
 * Property page (mobile): stay on web first.
 * 1) Modal — Continue App if installed, Get App if not (with close).
 * 2) After close — fixed Get App / Continue App bar under the logo header.
 */
const PropertyAppPrompt = ({
  propertyId,
  slug,
  isProject = false,
}) => {
  const platform = useMemo(() => (isIosMobile() ? 'ios' : 'android'), []);
  const storeLabel = platform === 'ios' ? 'Download on the App Store' : 'Get it on Google Play';

  const modalKey = `${MODAL_DISMISS_PREFIX}${propertyId || 'x'}`;
  const barKey = `${BAR_DISMISS_PREFIX}${propertyId || 'x'}`;
  const barRef = useRef(null);

  const [ready, setReady] = useState(false);
  // Optimistic: sync mark / post-store return before async GIRA finishes.
  const [installed, setInstalled] = useState(() => isPifAppInstalledSync());
  const [showModal, setShowModal] = useState(false);
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;

    const applyKnown = (known) => {
      if (cancelled) return;
      setInstalled(!!known || isPifAppInstalledSync());
    };

    const init = async () => {
      try {
        if (!isMobileWeb() && !isAndroidMobile() && !isIosMobile()) {
          if (!cancelled) setReady(true);
          return;
        }

        let modalDismissed = false;
        let barDismissed = false;
        try {
          modalDismissed = sessionStorage.getItem(modalKey) === '1';
          barDismissed = sessionStorage.getItem(barKey) === '1';
        } catch {
          /* ignore */
        }

        const known = await isPifAppInstalled();
        if (cancelled) return;

        applyKnown(known);
        setReady(true);

        if (!modalDismissed) {
          setShowModal(true);
          setShowBar(false);
        } else if (!barDismissed) {
          setShowModal(false);
          setShowBar(true);
        }

        // DAL / GIRA can lag after assetlinks is fixed — keep watching briefly.
        if (!known && isAndroidMobile()) {
          watchForPifAppInstall({ timeoutMs: 25000, intervalMs: 2000, signal: ac?.signal })
            .then((found) => {
              if (found) applyKnown(true);
            })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) {
          setReady(true);
          setShowModal(true);
        }
      }
    };

    init();

    const launchOpts = () => ({ propertyId, slug, isProject });
    const unsubscribe = subscribePifInstall((known) => {
      if (!cancelled) setInstalled(!!known || isPifAppInstalledSync());
    }, launchOpts);

    return () => {
      cancelled = true;
      try { ac?.abort(); } catch { /* ignore */ }
      unsubscribe();
    };
  }, [modalKey, barKey, propertyId, slug, isProject]);

  /*
   * Fixed Get App bar under the 56px logo. Measure bar height and set one
   * --pap-stack-h on the page (logo + bar). No extra topbar margin.
   */
  useEffect(() => {
    const active = showBar && !showModal;

    if (!active) {
      document.body.classList.remove('pap-bar-active');
      document.body.style.removeProperty('--pap-bar-h');
      document.body.style.removeProperty('--pap-stack-h');
      return undefined;
    }

    document.body.classList.add('pap-bar-active');

    const HEADER_H = 56;
    const sync = () => {
      const barH = Math.round(barRef.current?.getBoundingClientRect().height || 52);
      document.body.style.setProperty('--pap-bar-h', `${barH}px`);
      document.body.style.setProperty('--pap-stack-h', `${HEADER_H + barH}px`);
    };

    sync();
    const t = window.setTimeout(sync, 0);

    let ro;
    if (typeof ResizeObserver !== 'undefined' && barRef.current) {
      ro = new ResizeObserver(sync);
      ro.observe(barRef.current);
    }
    window.addEventListener('resize', sync);

    return () => {
      document.body.classList.remove('pap-bar-active');
      document.body.style.removeProperty('--pap-bar-h');
      document.body.style.removeProperty('--pap-stack-h');
      window.clearTimeout(t);
      window.removeEventListener('resize', sync);
      try { ro?.disconnect(); } catch { /* ignore */ }
    };
  }, [showBar, showModal]);

  const closeModal = () => {
    try {
      sessionStorage.setItem(modalKey, '1');
    } catch {
      /* ignore */
    }
    setShowModal(false);
    try {
      if (sessionStorage.getItem(barKey) !== '1') setShowBar(true);
    } catch {
      setShowBar(true);
    }
  };

  const dismissBar = () => {
    try {
      sessionStorage.setItem(barKey, '1');
    } catch {
      /* ignore */
    }
    setShowBar(false);
  };

  const onOpenApp = (e) => {
    e?.preventDefault?.();
    openContinueApp({
      knownInstalled: true,
      propertyId,
      slug,
      isProject,
      storeFallback: false,
    });
  };

  const onGetApp = (e) => {
    e?.preventDefault?.();
    openContinueApp({
      knownInstalled: false,
      propertyId,
      slug,
      isProject,
      storeFallback: true,
    });
  };

  if (!ready) return null;
  if (!showModal && !showBar) return null;

  return (
    <>
      {showModal && (
        <div className="pap-overlay" role="presentation" onClick={closeModal}>
          <div
            className="pap-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pap-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="pap-close" onClick={closeModal} aria-label="Close">
              ×
            </button>

            <div className="pap-badge">{platform === 'ios' ? 'iOS' : 'Android'}</div>

            {installed ? (
              <>
                <h2 id="pap-title" className="pap-title">Continue App</h2>
                <p className="pap-desc">
                  You have the PIF app installed. Open this property there for a better experience.
                </p>
                <button type="button" className="pap-primary" onClick={onOpenApp}>
                  Continue App
                </button>
              </>
            ) : (
              <>
                <h2 id="pap-title" className="pap-title">Get App for more experience</h2>
                <p className="pap-desc">
                  Download the Properties Infinite app for faster browsing, alerts, and exclusive listings.
                </p>
                <button type="button" className="pap-primary" onClick={onGetApp}>
                  {storeLabel}
                </button>
                <button type="button" className="pap-secondary" onClick={onOpenApp}>
                  Already installed? Continue App
                </button>
              </>
            )}

            <button type="button" className="pap-secondary" onClick={closeModal}>
              Continue on web
            </button>
          </div>
        </div>
      )}

      {showBar && !showModal && (
        <div className="pap-bar" ref={barRef} role="region" aria-label="Download the App">
          <div className="pap-bar-inner">
            <div className="pap-app-icon" aria-hidden="true">
              <svg className="pap-app-icon__phone" viewBox="0 0 36 48" fill="none" aria-hidden="true">
                <rect x="3.5" y="1.5" width="29" height="45" rx="5.5" stroke="#1a472a" strokeWidth="2" />
                <rect x="13" y="4" width="10" height="2" rx="1" fill="#1a472a" />
              </svg>
              <span className="pap-app-icon__screen">
                <img src={appIcon} alt="" className="pap-app-icon__img" />
                <span className="pap-app-icon__pif">PIF</span>
              </span>
            </div>

            <p className="pap-bar-text">
              {installed
                ? 'Continue in the app for a better experience!'
                : 'Get the app for a personalized experience!'}
            </p>

            {installed ? (
              <button type="button" className="pap-bar-btn" onClick={onOpenApp}>
                Continue App
              </button>
            ) : (
              <button type="button" className="pap-bar-btn" onClick={onGetApp}>
                Get App
              </button>
            )}

            <button
              type="button"
              className="pap-bar-dismiss"
              onClick={dismissBar}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyAppPrompt;
